import { useEffect, useState } from "react";
import { CalendarSync, Pencil, Play, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AgentSearchSelect } from "../components/AgentSearchSelect";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const EMPTY_FORM = { agent_code: "", nomor_layanan: "", day_of_month: "", amount: "", note: "" };

export default function BulananStarlink() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [subs, setSubs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    api.get("/starlink/subs").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/agents").then((r) => setAgents(r.data)).catch(() => {});
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const monthNow = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit" }).format(new Date());
  const activeSubs = subs.filter((s) => s.active);
  const totalBulanan = activeSubs.reduce((a, s) => a + (s.amount || 0), 0);
  const billedThisMonth = subs.filter((s) => s.last_billed === monthNow).length;

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/starlink/subs", { ...form, day_of_month: Number(form.day_of_month), amount: Number(form.amount) });
      toast.success("Tagihan Bulanan Starlink ditambahkan");
      setForm(EMPTY_FORM);
      setAddOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/starlink/subs/${editTarget.id}`, {
        nomor_layanan: editTarget.nomor_layanan, day_of_month: Number(editTarget.day_of_month),
        amount: Number(editTarget.amount), note: editTarget.note, active: !!editTarget.active,
      });
      toast.success("Tagihan diperbarui");
      setEditTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/starlink/subs/${s.id}`, {
        nomor_layanan: s.nomor_layanan, day_of_month: s.day_of_month,
        amount: s.amount, note: s.note, active: !s.active,
      });
      toast.success(s.active ? `Tagihan ${s.agent_code} dinonaktifkan` : `Tagihan ${s.agent_code} diaktifkan`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/starlink/subs/${deleteTarget.id}`);
      toast.success(`Tagihan ${deleteTarget.agent_code} dihapus`);
      setDeleteTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/starlink/run-now");
      toast.success(`Penagihan dijalankan — ${data.processed} tagihan jatuh tempo hari ini diproses`);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="starlink-forbidden">
        <ShieldCheck className="w-10 h-10 text-rose-500" />
        <p className="text-muted-foreground text-sm">Menu ini hanya untuk Super Admin & Admin Fasnet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="starlink-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Bulanan Starlink</h1>
          <p className="text-sm text-muted-foreground mt-1">Saldo agen yang ditautkan otomatis dipotong tiap bulan sesuai tanggal & nominal yang ditetapkan di sini. Bila saldo kurang, saldo menjadi minus dan tercatat di Piutang Saldo.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runNow} disabled={busy} data-testid="btn-run-now"><Play className="w-4 h-4 mr-1.5" /> Proses Tagihan Hari Ini</Button>
          <Button size="sm" onClick={() => { setForm(EMPTY_FORM); setAddOpen(true); }} data-testid="btn-add-sub"><Plus className="w-4 h-4 mr-1.5" /> Tambah Tagihan</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Card className="bg-card border-border shadow-sm"><CardContent className="p-3 sm:p-4 text-center">
          <div className="text-[10px] sm:text-xs text-muted-foreground">Tagihan Aktif</div>
          <div className="font-mono font-extrabold text-lg sm:text-2xl" data-testid="sub-summary-active">{activeSubs.length}</div>
        </CardContent></Card>
        <Card className="bg-card border-border shadow-sm"><CardContent className="p-3 sm:p-4 text-center">
          <div className="text-[10px] sm:text-xs text-muted-foreground">Total per Bulan</div>
          <div className="font-mono font-extrabold text-lg sm:text-2xl text-emerald-400" data-testid="sub-summary-total">{rupiah(totalBulanan)}</div>
        </CardContent></Card>
        <Card className="bg-card border-border shadow-sm"><CardContent className="p-3 sm:p-4 text-center">
          <div className="text-[10px] sm:text-xs text-muted-foreground">Terpotong Bulan Ini</div>
          <div className="font-mono font-extrabold text-lg sm:text-2xl text-sky-400" data-testid="sub-summary-billed">{billedThisMonth}</div>
        </CardContent></Card>
      </div>

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="starlink-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><CalendarSync className="w-4 h-4 text-sky-500" /> Daftar Tagihan Bulanan</CardTitle></CardHeader>
        <Table className="min-w-[860px]" data-testid="starlink-table">
          <TableHeader>
            <TableRow>
              <TableHead>Agen</TableHead><TableHead>Layanan</TableHead><TableHead className="text-center">Tgl Tagihan</TableHead>
              <TableHead className="text-right">Nominal</TableHead><TableHead>Terakhir Dipotong</TableHead>
              <TableHead className="text-right">Saldo Agen</TableHead><TableHead className="text-center">Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!subs.length && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="starlink-empty">Belum ada tagihan Bulanan Starlink — klik "Tambah Tagihan"</TableCell></TableRow>
            )}
            {subs.map((s) => (
              <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                <TableCell>
                  <div className="font-medium text-sm"><span className="font-mono">{s.agent_code}</span> {s.agent_name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{s.email}</div>
                </TableCell>
                <TableCell>
                  <div className="text-xs font-mono">{s.nomor_layanan || "-"}</div>
                  {s.note && <div className="text-[10px] text-muted-foreground">{s.note}</div>}
                </TableCell>
                <TableCell className="text-center font-mono font-bold text-sm">tgl {s.day_of_month}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-emerald-400">{rupiah(s.amount)}</TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{s.last_billed || "belum pernah"}</TableCell>
                <TableCell className={`text-right font-mono text-xs ${s.balance < 0 ? "text-rose-400" : ""}`}>{rupiah(s.balance || 0)}</TableCell>
                <TableCell className="text-center">
                  <button type="button" onClick={() => toggleActive(s)}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-colors ${s.active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-slate-500/10 text-slate-400 border-slate-500/30"}`}
                    data-testid={`btn-toggle-active-${s.id}`}>{s.active ? "Aktif" : "Nonaktif"}</button>
                </TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setEditTarget({ ...s })} data-testid={`btn-edit-sub-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="text-rose-400 border-rose-500/30" onClick={() => setDeleteTarget(s)} data-testid={`btn-del-sub-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-sub">
          <DialogHeader><DialogTitle className="text-base font-heading">Tambah Tagihan Bulanan Starlink</DialogTitle></DialogHeader>
          <form onSubmit={submitAdd} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Agen yang ditautkan</Label>
              <AgentSearchSelect
                options={agents.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, search: `${a.code} ${a.name}` }))}
                value={form.agent_code} onChange={(v) => setForm({ ...form, agent_code: v })} placeholder="Pilih agen" testid="select-sub-agent" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nomor Layanan / Nama Pelanggan (opsional)</Label>
              <Input value={form.nomor_layanan} onChange={(e) => setForm({ ...form, nomor_layanan: e.target.value })} placeholder="cth: SL-0231 / Bpk. Hendra" data-testid="input-sub-layanan" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tanggal tagihan tiap bulan (1-28)</Label>
                <Input type="number" min="1" max="28" required value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} placeholder="cth: 5" data-testid="input-sub-day" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nominal (Rp)</Label>
                <Input type="number" min="1000" step="1000" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="cth: 350000" data-testid="input-sub-amount" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Catatan (opsional)</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="cth: paket Priority 1TB" data-testid="input-sub-note" />
            </div>
            <Button type="submit" className="w-full" disabled={busy || !form.agent_code} data-testid="btn-submit-sub">{busy ? "Menyimpan..." : "Simpan Tagihan"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-sub-edit">
          <DialogHeader><DialogTitle className="text-base font-heading">Edit Tagihan — {editTarget?.agent_code} {editTarget?.agent_name}</DialogTitle></DialogHeader>
          {editTarget && (
            <form onSubmit={submitEdit} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Nomor Layanan / Nama Pelanggan</Label>
                <Input value={editTarget.nomor_layanan} onChange={(e) => setEditTarget({ ...editTarget, nomor_layanan: e.target.value })} data-testid="input-edit-layanan" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Tanggal tagihan (1-28)</Label>
                  <Input type="number" min="1" max="28" required value={editTarget.day_of_month} onChange={(e) => setEditTarget({ ...editTarget, day_of_month: e.target.value })} data-testid="input-edit-day" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nominal (Rp)</Label>
                  <Input type="number" min="1000" step="1000" required value={editTarget.amount} onChange={(e) => setEditTarget({ ...editTarget, amount: e.target.value })} data-testid="input-edit-amount" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Catatan</Label>
                <Input value={editTarget.note} onChange={(e) => setEditTarget({ ...editTarget, note: e.target.value })} data-testid="input-edit-note" />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="btn-submit-edit-sub">{busy ? "Menyimpan..." : "Simpan Perubahan"}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="dialog-sub-delete">
          <DialogHeader><DialogTitle className="text-base font-heading">Hapus Tagihan?</DialogTitle></DialogHeader>
          {deleteTarget && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Tagihan Bulanan Starlink <b className="text-foreground">{deleteTarget.agent_code} {deleteTarget.agent_name}</b> ({rupiah(deleteTarget.amount)} tiap tgl {deleteTarget.day_of_month}) akan dihapus dan tidak lagi memotong saldo otomatis.</p>
              <div className="flex gap-2">
                <Button variant="destructive" className="flex-1" onClick={doDelete} disabled={busy} data-testid="btn-del-confirm">{busy ? "Menghapus..." : "Ya, Hapus"}</Button>
                <Button variant="outline" className="flex-1" onClick={() => setDeleteTarget(null)} data-testid="btn-del-cancel">Batal</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
